import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    PagesContentDTO,
    useDeletePagesContentApi,
    useSavePagesContentApi,
    useSearchPagesContentApi,
} from '../../../api/pages-content-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SavePagesContentModal } from './save-pages-content-modal';

export function PagesContent({ pageId }: { pageId: string }) {
    const [lastPageId, setLastPageId] = useState<string>();
    const [showModal, setShowModal] = useState(false);
    const [selectedPagesContent, setSelectedPagesContent] = useState<PagesContentDTO | undefined>();
    const pagesContentInit = useSearchPagesContentApi();
    const savePagesContent = useSavePagesContentApi();
    const deletePagesContent = useDeletePagesContentApi();

    const { result, error, busy, initialized, initializing } = pagesContentInit;

    const columns: IBasicTableColumn<PagesContentDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 40,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>heading</T>,
                name: 'heading',
                Cell: ({ row }) => (
                    <bd.Flex align="center" gap={2}>
                        <span>
                            (<N>{row.sectionOrder}</N>)
                        </span>
                        <span>{row.heading}</span>
                    </bd.Flex>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        if (lastPageId === pageId && (initialized || initializing || error)) return;

        pagesContentInit.get({ pageId }).then(() => {
            setLastPageId(pageId);
        });
    }, [initialized, initializing, error, pagesContentInit, pageId, lastPageId]);

    const handleDelete = (pagesContent: PagesContentDTO) => {
        bd.msgbox(
            translate('delete-pages-content'),
            translate('confirm-delete-pages-content'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deletePagesContent.remove(
                            pagesContent,
                            () => {
                                pagesContentInit.updateResult({
                                    ...result,
                                    pagesContents: result.pagesContents.filter(
                                        b => b.id !== pagesContent.id
                                    ),
                                });
                                notify.dark(
                                    translate('selected-pages-content-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedPagesContent(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (pagesContent: PagesContentDTO) => {
        savePagesContent.post(
            null,
            pagesContent,
            res => {
                if (selectedPagesContent) {
                    pagesContentInit.updateResult({
                        ...result,
                        pagesContents: result.pagesContents.map(b =>
                            b.id === pagesContent.id ? res.pagesContent : b
                        ),
                    });
                } else {
                    pagesContentInit.updateResult({
                        ...result,
                        pagesContents: [res.pagesContent, ...result.pagesContents],
                    });
                }

                setShowModal(false);
                setSelectedPagesContent(undefined);
                notify.success(
                    selectedPagesContent
                        ? translate('selected-pages-content-updated-successfully')
                        : translate('new-pages-content-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => pagesContentInit.post(null, {})} />;
    if (!initialized || initializing || lastPageId !== pageId) return <Loading />;

    return (
        <TProvider>
            <div>
                <PageNavbar
                    title="pages-content"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-pages-content</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedPagesContent(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <div className="h-100">
                    <bd.BasicTable
                        className="mx-n3"
                        columns={columns}
                        data={result.pagesContents}
                        onRowClick={({ row }) => {
                            setSelectedPagesContent(row);
                            setShowModal(true);
                        }}
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SavePagesContentModal
                        show={showModal}
                        selectedPageId={pageId}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedPagesContent(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedPagesContent={selectedPagesContent}
                        allowEdit={result.allowEdit}
                    />
                </div>
            </div>
        </TProvider>
    );
}
